import { InboxItem } from './types';

/**
 * How the admin tabs of one browser agree on where an alert goes (plan §4).
 *
 * One tab — the leader, elected with `navigator.locks` — watches for new
 * messages and decides; every tab tells the others, over a BroadcastChannel,
 * whether it is focused, whether it is visible and which thread its dock has
 * open. No server is involved, and a closed leader hands the lock on by itself.
 */

export const ALERT_LOCK = 'cleo-inbox-alerts';
const CHANNEL_NAME = 'cleo-inbox';

/** What one tab reports about itself. */
export interface TabState {
  tabId: string;
  focused: boolean;
  visible: boolean;
  /** The conversation open in this tab's dock, while the dock is showing it. */
  openKey: string | null;
  /** Last time this tab had focus — picks the tab when none has it now. */
  focusedAt: number;
  closed?: boolean;
}

/** Who took an unclaimed lead, once somebody has. */
export interface AlertClaim {
  id: string;
  name: string | null;
  /** 'manual' reads "Assigned to …", anything else "… took it". */
  source: string | null;
}

/** One alert, as the tab that shows it needs it. Keyed by conversation. */
export interface MessageAlert {
  key: string;
  commId: string;
  item: InboxItem;
  /** Mine, or nobody's (offered with Take it). */
  kind: 'mine' | 'unclaimed';
  claim: AlertClaim | null;
  sound: boolean;
  /** Messages merged into this toast while it has been up (T-076); 1 for a new one. */
  count: number;
  /** When the first of them arrived. */
  firstAt: string;
}

export type BusMessage =
  | { type: 'state'; state: TabState }
  | { type: 'hello' }
  | { type: 'toast'; to: string; alert: MessageAlert }
  | { type: 'alert-update'; key: string; claim: AlertClaim }
  /** Handled elsewhere (a reply, Done, a snooze): say so, then go. */
  | { type: 'alert-clear'; key: string; reason?: string }
  /** Closed by the user in some tab (✕, Open, Done): gone from every tab. */
  | { type: 'toast-closed'; key: string }
  /** Clear all, in some tab: every toast in every tab. */
  | { type: 'toasts-cleared' }
  /** The conversation's toast moved to `keep` (a newer message went there). */
  | { type: 'toast-drop'; key: string; keep: string }
  /** A tab's toasts, for a new leader to keep watching (answers `hello`). */
  | { type: 'toasts-up'; tabId: string; alerts: MessageAlert[] }
  | { type: 'prefs' };

type Listener = (msg: BusMessage) => void;

class AlertBus {
  readonly tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  private channel: BroadcastChannel | null = null;
  private listeners = new Set<Listener>();

  constructor() {
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.onmessage = (e: MessageEvent<BusMessage>) => this.emit(e.data);
    }
  }

  /** To every OTHER tab. A tab never hears its own posts — see `local`. */
  post(msg: BusMessage) {
    this.channel?.postMessage(msg);
  }

  /** To the listeners in this tab, as if another tab had posted it. */
  local(msg: BusMessage) {
    this.emit(msg);
  }

  /** To every tab, this one included. */
  broadcast(msg: BusMessage) {
    this.post(msg);
    this.local(msg);
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private emit(msg: BusMessage) {
    this.listeners.forEach((fn) => fn(msg));
  }
}

let bus: AlertBus | null = null;

export function getAlertBus(): AlertBus {
  if (!bus) bus = new AlertBus();
  return bus;
}

/**
 * Where an alert goes, given every tab's latest state and the user's settings.
 * One place, at most once: a toast in the focused tab, else a desktop
 * notification, else (desktop unavailable) a toast in a visible tab, else
 * nothing — and nothing at all if a visible tab already has the thread open.
 */
export type AlertRoute =
  | { kind: 'toast'; tabId: string }
  | { kind: 'desktop' }
  | { kind: 'none'; reason: 'thread_open' | 'toasts_off' | 'no_visible_tab' };

export function routeAlert(
  key: string,
  tabs: TabState[],
  opts: { toasts: boolean; desktop: boolean },
): AlertRoute {
  const live = tabs.filter((t) => !t.closed);
  if (live.some((t) => t.visible && t.openKey === key)) {
    return { kind: 'none', reason: 'thread_open' };
  }
  const focused = live.find((t) => t.focused);
  if (focused) {
    if (opts.toasts) return { kind: 'toast', tabId: focused.tabId };
    // Toasts off but desktop on: the one channel the user left on.
    if (opts.desktop) return { kind: 'desktop' };
    return { kind: 'none', reason: 'toasts_off' };
  }
  if (opts.desktop) return { kind: 'desktop' };
  if (!opts.toasts) return { kind: 'none', reason: 'toasts_off' };
  const visible = live
    .filter((t) => t.visible)
    .sort((a, b) => b.focusedAt - a.focusedAt)[0];
  return visible ? { kind: 'toast', tabId: visible.tabId } : { kind: 'none', reason: 'no_visible_tab' };
}
